import { useState, useRef, useEffect, useMemo, type CSSProperties, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  usePubliekDossier, usePubliekBerichten, useSendPubliekBericht,
  usePubliekAfspraken, useBevestigAfspraak, useWeigerAfspraak,
  useMarkeerGelezen, usePortaalBranding, useSubmitFeedback, useSubmitSchade,
  usePubliekActiviteiten,
  type DossierPubliek, type BerichtPubliek, type AfspraakPubliek, type BrandingPubliek, type ActiviteitPubliek,
} from '../hooks/useKlantportaal.js';
import { PLATFORM_NAAM, merkVariabelen } from '../../../core/shared/kordaroBrand.js';
import { KIcon, type KIconNaam } from '../../../core/shared/components/KIcon.js';
import { Faseband } from '../../../core/shared/components/Faseband.js';
import {
  dossierStatus, apparaatStatus, afspraakStatus, documentType, stapNaam, zonderEmoji, datumRegel, initialen,
  APPARAAT_VOLGORDE, type Toon, type DatumRegel,
} from '../utils/portaalWeergave.js';

/*
 * Dossierscherm van de klant (Kordaro v4, E01–E03), gsm-eerst. Kent geen route of token: het krijgt het
 * dossier, de winkelbranding en `api` (basispad van de dossier-API) als props. KlantportaalPage is de
 * dunne laag voor /portaal/:token, MijnApp voor /mijn/dossier/:id.
 * Gsm: vier bestemmingen (Mijn keuken, Berichten, Documenten, Meer). Desktop (≥768): alles op één pagina,
 * gesprek rechts. Winkelbranding (logo, naam, kleur via merkVariabelen) blijft. Interne notities komen nooit
 * mee: de server filtert ze (messageType ≠ internal); de pagina toont enkel wat de API teruggeeft.
 * Niet gebouwd (bestaat niet in het product): akkoord op een gedeeltelijke levering, restpunten, keukenfoto.
 */

// ─── Helpers ───────────────────────────────────────────────

const TZ = 'Europe/Brussels';
function datumLang(s: string | null): string {
  return s ? new Date(s).toLocaleDateString('nl-BE', { timeZone: TZ, day: 'numeric', month: 'long', year: 'numeric' }) : '—';
}
function dag(s: string): string {
  return new Date(s).toLocaleDateString('nl-BE', { timeZone: TZ, weekday: 'long', day: 'numeric', month: 'long' });
}
function tijdstip(s: string): string {
  const d = new Date(s);
  const nu = new Date();
  const tijd = d.toLocaleTimeString('nl-BE', { timeZone: TZ, hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === nu.toDateString()) return `Vandaag ${tijd}`;
  const gisteren = new Date(nu);
  gisteren.setDate(gisteren.getDate() - 1);
  if (d.toDateString() === gisteren.toDateString()) return `Gisteren ${tijd}`;
  return `${d.toLocaleDateString('nl-BE', { timeZone: TZ, day: 'numeric', month: 'short' })} ${tijd}`;
}

const TOON_KLASSE: Record<Toon, string> = {
  neutraal: 'k-status', info: 'k-status k-status--info', success: 'k-status k-status--success',
  warning: 'k-status k-status--warning', danger: 'k-status k-status--danger',
};
const TOON_ICOON: Record<Toon, KIconNaam | null> = { neutraal: null, info: 'info', success: 'vink', warning: 'klok', danger: 'let' };
function Status({ tekst, toon }: { tekst: string; toon: Toon }) {
  const icoon = TOON_ICOON[toon];
  return <span className={TOON_KLASSE[toon]}>{icoon && <KIcon naam={icoon} size={16} />}{tekst}</span>;
}

function useIsDesktop() {
  const query = '(min-width: 768px)';
  const [ok, setOk] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const zet = () => setOk(mq.matches);
    mq.addEventListener('change', zet);
    return () => mq.removeEventListener('change', zet);
  }, []);
  return ok;
}

function Sectie({ titel, id, actie, children }: { titel: string; id: string; actie?: ReactNode; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h2 id={id} className="k-section-title">{titel}</h2>
        {actie}
      </div>
      {children}
    </section>
  );
}

// ─── Kop ───────────────────────────────────────────────────

function PortaalKop({ dossier, branding, compact }: { dossier: DossierPubliek; branding: BrandingPubliek | null | undefined; compact?: boolean }) {
  const winkel = branding?.bedrijfsNaam || dossier.entity.name || PLATFORM_NAAM;
  const st = dossierStatus(dossier.status);
  if (compact) {
    return (
      <header className="k-shell-header">
        <div className="px-4 py-2 min-h-[56px] flex items-center justify-between gap-3">
          {branding?.logoUrl
            ? <img src={branding.logoUrl} alt={winkel} className="max-h-8 max-w-[140px] object-contain" />
            : <span className="font-semibold truncate" style={{ color: 'var(--k-brand)' }}>{winkel}</span>}
          <span className="k-meta truncate">{dossier.klantNaam}</span>
        </div>
      </header>
    );
  }
  return (
    <header className="k-shell-header">
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between gap-3 min-h-[40px]">
          {branding?.logoUrl ? (
            <img src={branding.logoUrl} alt={winkel} className="max-h-10 max-w-[180px] object-contain" />
          ) : (
            <span className="text-lg font-semibold" style={{ color: 'var(--k-brand)' }}>{winkel}</span>
          )}
          <span aria-hidden="true" className="w-11 h-11 rounded-full grid place-items-center text-sm font-semibold" style={{ background: 'var(--k-selected)', color: 'var(--k-on-selected)' }}>
            {initialen(dossier.klantNaam)}
          </span>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="k-title">{dossier.klantNaam}</h1>
          <Status tekst={st.tekst} toon={st.toon} />
        </div>
        <p className="k-meta mt-1">Mijn keuken · dossier {dossier.dossierNummer} · {dossier.adres}, {dossier.postcode} {dossier.stad}</p>
      </div>
    </header>
  );
}

// ─── Mijn keuken ───────────────────────────────────────────

function DatumRij({ icoon, regel, extra }: { icoon: KIconNaam; regel: DatumRegel; extra?: string }) {
  return (
    <li className="k-row flex items-start gap-3">
      <span className="mt-0.5 text-[var(--k-muted)]"><KIcon naam={icoon} size={24} /></span>
      <div className="min-w-0">
        <p className="font-semibold">{regel.label}</p>
        {regel.datum ? (
          <p className="text-[var(--k-text)]">{dag(regel.datum)}{extra ? ` · ${extra}` : ''}</p>
        ) : (
          <p className="k-meta">— {regel.reden}</p>
        )}
      </div>
    </li>
  );
}

function AfspraakKeuze({ afspraak, api }: { afspraak: AfspraakPubliek; api: string }) {
  const bevestig = useBevestigAfspraak(api);
  const weiger = useWeigerAfspraak(api);
  const [gekozen, setGekozen] = useState<string | null>(null);
  const [zonderKeuze, setZonderKeuze] = useState(false);
  const bezig = bevestig.isPending || weiger.isPending;
  const groep = `keuze-${afspraak.id}`;

  return (
    <div className="k-panel space-y-4" style={{ borderColor: 'var(--k-brand)' }}>
      <div className="flex gap-3">
        <span className="mt-0.5" style={{ color: 'var(--k-brand)' }}><KIcon naam="gesprekken" size={24} /></span>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold leading-7">We hebben uw antwoord nodig</h3>
          <p>{afspraak.titel} — kies de datum die u het best past.</p>
          {afspraak.beschrijving && <p className="k-meta mt-1">{afspraak.beschrijving}</p>}
          <p className="k-meta mt-1">Voorgesteld door {afspraak.aanmaker.firstName} {afspraak.aanmaker.lastName}</p>
        </div>
      </div>
      <fieldset aria-describedby={zonderKeuze ? `${groep}-fout` : undefined}>
        <legend className="k-label mb-2">Voorgestelde data</legend>
        <div className="space-y-2">
          {afspraak.datums.map((d) => (
            <label key={d.id} className={clsx('flex items-center gap-3 min-h-[48px] px-3 rounded-[var(--k-radius-control)] border cursor-pointer bg-[var(--k-surface)]',
              gekozen === d.id ? 'border-[var(--k-brand)] bg-[var(--k-selected)]' : 'border-[var(--k-control-border)]')}>
              <input type="radio" name={groep} value={d.id} checked={gekozen === d.id} onChange={() => { setGekozen(d.id); setZonderKeuze(false); }}
                className="w-5 h-5 accent-[var(--k-brand)]" />
              <span>
                <span className="block font-medium">{dag(d.datum)}</span>
                {(d.vanUur || d.totUur) && <span className="k-meta block">{d.vanUur}{d.totUur ? `–${d.totUur}` : ''}</span>}
              </span>
            </label>
          ))}
        </div>
        {zonderKeuze && <p id={`${groep}-fout`} className="k-error mt-2">Kies eerst een datum.</p>}
      </fieldset>
      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" className="k-button flex-1" aria-busy={bevestig.isPending || undefined} disabled={bezig}
          onClick={() => { if (!gekozen) { setZonderKeuze(true); return; } bevestig.mutate({ afspraakId: afspraak.id, datumId: gekozen }); }}>
          {bevestig.isPending ? 'Bezig…' : 'Deze datum bevestigen'}
        </button>
        <button type="button" className="k-button k-button--secondary flex-1" aria-busy={weiger.isPending || undefined} disabled={bezig}
          onClick={() => weiger.mutate(afspraak.id)}>
          {weiger.isPending ? 'Bezig…' : 'Geen van deze data past'}
        </button>
      </div>
      <p aria-live="polite" className="k-error">
        {(bevestig.isError || weiger.isError) ? 'Uw antwoord is niet doorgekomen. Er is nog niets bevestigd; probeer opnieuw.' : ''}
      </p>
    </div>
  );
}

function AfsprakenRijen({ afspraken }: { afspraken: AfspraakPubliek[] }) {
  if (afspraken.length === 0) return <p className="k-meta">Er zijn nog geen afspraken gepland.</p>;
  return (
    <ul>
      {afspraken.map((a) => {
        const st = afspraakStatus(a.status);
        return (
          <li key={a.id} className="k-row flex items-start gap-3">
            <span className="mt-0.5 text-[var(--k-muted)]"><KIcon naam="planning" size={24} /></span>
            <div className="min-w-0 flex-1">
              <p className="font-semibold">{a.titel}</p>
              <p className="k-meta">{stapNaam(a.trajectstap.template.naam)} · {a.aanmaker.firstName} {a.aanmaker.lastName}</p>
              {a.status === 'bevestigd' && a.bevestigdeDatum && <p>{dag(a.bevestigdeDatum)}</p>}
              {a.status === 'geweigerd' && <p className="k-meta">U gaf aan dat geen van de data past. De winkel stelt nieuwe data voor.</p>}
            </div>
            <Status tekst={st.tekst} toon={st.toon} />
          </li>
        );
      })}
    </ul>
  );
}

function Toestellen({ apparaten }: { apparaten: DossierPubliek['apparaten'] }) {
  if (apparaten.length === 0) return null;
  const geleverd = apparaten.filter((a) => a.status === 'GELEVERD').length;
  const lijst = [...apparaten].sort((a, b) => (APPARAAT_VOLGORDE[a.status] ?? 0) - (APPARAAT_VOLGORDE[b.status] ?? 0));
  return (
    <Sectie titel="Toestellen" id="k-toestellen" actie={<span className="k-meta">{geleverd} van {apparaten.length} geleverd</span>}>
      <ul>
        {lijst.map((a) => {
          const st = apparaatStatus(a.status);
          return (
            <li key={a.id} className="k-row flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium">{a.naam}</p>
                <p className="k-meta">{[a.merk, a.binnenDatum ? `in de winkel sinds ${datumLang(a.binnenDatum)}` : null].filter(Boolean).join(' · ') || '—'}</p>
              </div>
              <Status tekst={st.tekst} toon={st.toon} />
            </li>
          );
        })}
      </ul>
    </Sectie>
  );
}

function MijnKeuken({ dossier, afspraken, api, winkel, desktop, naarBerichten, naarDocumenten }: {
  dossier: DossierPubliek; afspraken: AfspraakPubliek[]; api: string; winkel: string; desktop: boolean;
  naarBerichten: () => void; naarDocumenten: () => void;
}) {
  const open = afspraken.filter((a) => a.status === 'voorstel');
  const overige = afspraken.filter((a) => a.status !== 'voorstel');
  const levering = datumRegel('levering', dossier.leveringsdatum, dossier.trajectstappen, afspraken);
  const plaatsing = datumRegel('plaatsing', dossier.plaatsingsdatumStart, dossier.trajectstappen, afspraken);
  const plaatsingTot = dossier.plaatsingsdatumEind && plaatsing.soort === 'verwacht' && dossier.plaatsingsdatumEind.slice(0, 10) !== dossier.plaatsingsdatumStart?.slice(0, 10)
    ? `tot ${dag(dossier.plaatsingsdatumEind)}` : undefined;
  const vertraagd = levering.soort === 'verwacht' && !!levering.datum && new Date(levering.datum) < new Date() && dossier.status !== 'afgerond';
  const alleGeleverd = dossier.apparaten.length > 0 && dossier.apparaten.every((a) => a.status === 'GELEVERD');
  const toonFeedback = alleGeleverd || dossier.status === 'afgerond';
  const fasen = dossier.trajectstappen.map((s) => ({ id: s.id, naam: stapNaam(s.template.naam), status: s.status, datum: s.status === 'completed' ? s.afgerondDatum : s.geplandeDatum }));

  return (
    <div className="space-y-8">
      {open.length > 0 && (
        <section aria-label="Uw antwoord nodig" className="space-y-3">
          {open.map((a) => <AfspraakKeuze key={a.id} afspraak={a} api={api} />)}
        </section>
      )}

      <section aria-label="Planning" >
        {vertraagd && (
          <p className="k-status k-status--warning !flex !items-start !py-2 !px-3 mb-2">
            <KIcon naam="let" size={16} className="mt-0.5" />
            <span>De verwachte leverdatum ({datumLang(levering.datum)}) is voorbij. Neem contact op met {winkel} voor de nieuwe datum.</span>
          </p>
        )}
        <ul className="border-t border-[var(--k-border)]">
          <DatumRij icoon="logistiek" regel={levering} />
          <DatumRij icoon="plaatsing" regel={plaatsing} extra={plaatsingTot} />
        </ul>
      </section>

      {fasen.length > 0 && (
        <Sectie titel="Waar staat uw keuken?" id="k-fasen">
          <Faseband stappen={fasen} titel="Fasen van uw keuken" />
        </Sectie>
      )}

      {(overige.length > 0 || open.length === 0) && (
        <Sectie titel="Afspraken" id="k-afspraken">
          <AfsprakenRijen afspraken={overige} />
        </Sectie>
      )}

      <Toestellen apparaten={dossier.apparaten} />

      {toonFeedback && <LeveringFeedback api={api} winkel={winkel} />}

      <div className="flex flex-col sm:flex-row gap-2">
        <button type="button" onClick={naarBerichten} className={clsx('k-button flex-1', open.length > 0 && 'k-button--secondary')}>
          <KIcon naam="gesprekken" /> Vraag stellen
        </button>
        {!desktop && (
          <button type="button" onClick={naarDocumenten} className="k-button k-button--secondary flex-1">
            <KIcon naam="documenten" /> Documenten
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Feedback + schade ─────────────────────────────────────

function SchadeMelden({ api, winkel, onClose }: { api: string; winkel: string; onClose: () => void }) {
  const submit = useSubmitSchade(api);
  const [beschrijving, setBeschrijving] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [leegFout, setLeegFout] = useState(false);
  const [klaar, setKlaar] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const eerste = useRef<HTMLTextAreaElement>(null);
  const terug = useRef<HTMLElement | null>(null);
  const previews = useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  useEffect(() => {
    terug.current = document.activeElement as HTMLElement | null;
    eerste.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submit.isPending) onClose(); };
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('keydown', esc); terug.current?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function verstuur() {
    if (!beschrijving.trim()) { setLeegFout(true); eerste.current?.focus(); return; }
    submit.mutate({ beschrijving: beschrijving.trim(), fotos }, { onSuccess: () => setKlaar(true) });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:px-4" onClick={(e) => { if (e.target === e.currentTarget && !submit.isPending) onClose(); }}>
      <div role="dialog" aria-modal="true" aria-labelledby="k-schade-titel" className="k-ui w-full sm:max-w-[560px] max-h-[100dvh] sm:max-h-[90dvh] overflow-y-auto bg-[var(--k-surface)] rounded-t-[var(--k-radius-panel)] sm:rounded-[var(--k-radius-panel)] p-6 pb-[calc(24px+env(safe-area-inset-bottom))]">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 id="k-schade-titel" className="k-section-title">{klaar ? 'Schade gemeld' : 'Schade melden'}</h2>
          <button type="button" className="k-button k-button--icon" aria-label="Sluiten" onClick={onClose} disabled={submit.isPending}><KIcon naam="sluit" /></button>
        </div>
        {klaar ? (
          <div className="space-y-4" role="status">
            <p>Uw melding{fotos.length ? ` en ${fotos.length} foto${fotos.length > 1 ? "'s" : ''}` : ''} {fotos.length ? 'zijn' : 'is'} opgeslagen in uw dossier bij {winkel}. De winkel neemt contact met u op.</p>
            <button type="button" className="k-button w-full" onClick={onClose}>Sluiten</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="k-schade-tekst" className="k-label block mb-1">Wat is er beschadigd? <span aria-hidden="true">*</span></label>
              <textarea id="k-schade-tekst" ref={eerste} className="k-field" value={beschrijving} aria-invalid={leegFout || undefined}
                aria-describedby={leegFout ? 'k-schade-fout' : undefined}
                onChange={(e) => { setBeschrijving(e.target.value); setLeegFout(false); }} placeholder="Bijvoorbeeld: kras op de deur van de vaatwasser" />
              {leegFout && <p id="k-schade-fout" className="k-error mt-1">Beschrijf kort wat er beschadigd is.</p>}
            </div>
            <div>
              <p className="k-label mb-1">Foto&apos;s <span className="k-meta">(maximum 5)</span></p>
              <input ref={fileInput} type="file" accept="image/*" multiple className="k-sr-only" tabIndex={-1} aria-hidden="true"
                onChange={(e) => { const n = Array.from(e.target.files || []); setFotos((v) => [...v, ...n].slice(0, 5)); e.target.value = ''; }} />
              {fotos.length > 0 && (
                <ul className="flex flex-wrap gap-3 mb-3">
                  {previews.map((src, i) => (
                    <li key={src} className="relative">
                      <img src={src} alt={`Foto ${i + 1}`} className="w-20 h-20 object-cover rounded-[var(--k-radius-control)] border border-[var(--k-border)]" />
                      <button type="button" className="k-button k-button--icon !absolute -top-3 -right-3 !w-11 !min-h-[44px]" aria-label={`Foto ${i + 1} verwijderen`}
                        onClick={() => setFotos((v) => v.filter((_, j) => j !== i))}><KIcon naam="sluit" /></button>
                    </li>
                  ))}
                </ul>
              )}
              {fotos.length < 5 && (
                <button type="button" className="k-button k-button--secondary w-full" onClick={() => fileInput.current?.click()}>
                  <KIcon naam="fotos" /> Foto toevoegen
                </button>
              )}
            </div>
            <p aria-live="polite" className="k-error">{submit.isError ? 'Uw melding is niet verstuurd. Uw tekst en foto\'s staan er nog; probeer opnieuw.' : ''}</p>
            <div className="flex flex-col-reverse sm:flex-row gap-2">
              <button type="button" className="k-button k-button--secondary flex-1" onClick={onClose} disabled={submit.isPending}>Annuleren</button>
              <button type="button" className="k-button flex-1" onClick={verstuur} disabled={submit.isPending} aria-busy={submit.isPending || undefined}>
                {submit.isPending ? 'Bezig…' : 'Schade melden'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function LeveringFeedback({ api, winkel }: { api: string; winkel: string }) {
  const submit = useSubmitFeedback(api);
  const [modus, setModus] = useState<'keuze' | 'score' | 'klaar'>('keuze');
  const [score, setScore] = useState(0);
  const [opmerking, setOpmerking] = useState('');
  const [schadeOpen, setSchadeOpen] = useState(false);

  if (modus === 'klaar') {
    return <p className="k-status k-status--success !flex !py-2 !px-3" role="status"><KIcon naam="vink" size={16} /> Bedankt, uw beoordeling is verstuurd naar {winkel}.</p>;
  }
  return (
    <Sectie titel="Hoe verliep de levering?" id="k-feedback">
      {modus === 'keuze' && (
        <div className="flex flex-col sm:flex-row gap-2">
          <button type="button" className="k-button k-button--secondary flex-1" onClick={() => setModus('score')}><KIcon naam="vink" /> Alles goed</button>
          <button type="button" className="k-button k-button--secondary flex-1" onClick={() => setSchadeOpen(true)}><KIcon naam="fotos" /> Schade melden</button>
        </div>
      )}
      {modus === 'score' && (
        <div className="space-y-4">
          <fieldset>
            <legend className="k-label mb-2">Uw score, van 1 (slecht) tot 5 (uitstekend)</legend>
            <div className="k-segment">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" className="k-chip min-w-[48px]" aria-pressed={score === n} onClick={() => setScore(n)}>{n}</button>
              ))}
            </div>
          </fieldset>
          <div>
            <label htmlFor="k-feedback-tekst" className="k-label block mb-1">Opmerking (niet verplicht)</label>
            <textarea id="k-feedback-tekst" className="k-field" value={opmerking} onChange={(e) => setOpmerking(e.target.value)} />
          </div>
          <p aria-live="polite" className="k-error">{submit.isError ? 'Uw beoordeling is niet verstuurd. Probeer opnieuw.' : ''}</p>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <button type="button" className="k-button k-button--secondary flex-1" onClick={() => setModus('keuze')}>Terug</button>
            <button type="button" className="k-button flex-1" disabled={score === 0 || submit.isPending} aria-busy={submit.isPending || undefined}
              onClick={() => submit.mutate({ score, comment: opmerking || undefined }, { onSuccess: () => setModus('klaar') })}>
              {submit.isPending ? 'Bezig…' : 'Beoordeling versturen'}
            </button>
          </div>
          {score === 0 && <p className="k-meta">Kies eerst een score.</p>}
        </div>
      )}
      {schadeOpen && <SchadeMelden api={api} winkel={winkel} onClose={() => setSchadeOpen(false)} />}
    </Sectie>
  );
}

// ─── Documenten ────────────────────────────────────────────

function Documenten({ documenten, api, winkel }: { documenten: DossierPubliek['documenten']; api: string; winkel: string }) {
  return (
    <Sectie titel="Documenten" id="k-documenten" actie={documenten.length > 0 ? <span className="k-meta">{documenten.length}</span> : undefined}>
      {documenten.length === 0 ? (
        <div className="k-empty k-panel">
          <p className="k-empty__title">Nog geen documenten</p>
          <p>{winkel} heeft nog geen documenten met u gedeeld. Mist u iets, bijvoorbeeld uw keukenplan? Stel de vraag via Berichten.</p>
        </div>
      ) : (
        <ul className="border-t border-[var(--k-border)]">
          {documenten.map((doc) => (
            <li key={doc.id}>
              <a href={`${api}/documenten/${doc.id}/download`} target="_blank" rel="noopener noreferrer"
                className="k-row flex items-center gap-3 no-underline text-[var(--k-text)] hover:bg-[var(--k-surface)] px-1">
                <span className="text-[var(--k-muted)]"><KIcon naam="documenten" size={24} /></span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium break-words">{doc.naam}</span>
                  <span className="k-meta block">
                    {documentType(doc.documentType)} · {datumLang(doc.createdAt)}
                    {doc.ondertekendOp ? ` · ondertekend op ${datumLang(doc.ondertekendOp)}` : ''}
                  </span>
                </span>
                <span className="k-link inline-flex items-center gap-1 shrink-0"><KIcon naam="download" size={20} /> Openen<span className="k-sr-only"> {doc.naam} (opent in een nieuw venster)</span></span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Sectie>
  );
}

// ─── Meer ──────────────────────────────────────────────────

function Activiteiten({ api }: { api: string }) {
  const { data: activiteiten = [], isLoading, isError } = usePubliekActiviteiten(api);
  return (
    <Sectie titel="Wat er gebeurde" id="k-activiteiten">
      {isLoading ? <p className="k-meta" role="status">Bezig met laden…</p>
        : isError ? <p className="k-meta">De geschiedenis kan nu niet geladen worden.</p>
          : activiteiten.length === 0 ? <p className="k-meta">Nog niets te melden.</p> : (
            <ul className="border-t border-[var(--k-border)]">
              {activiteiten.map((a: ActiviteitPubliek) => (
                <li key={a.id} className="k-row !min-h-0 !py-3">
                  <p>{zonderEmoji(a.tekst)}</p>
                  <p className="k-meta">{tijdstip(a.tijdstip)}</p>
                </li>
              ))}
            </ul>
          )}
    </Sectie>
  );
}

function Meer({ dossier, branding, api, winkel, extra }: { dossier: DossierPubliek; branding: BrandingPubliek | null | undefined; api: string; winkel: string; extra?: ReactNode }) {
  const tel = branding?.telefoon || dossier.entity.phone;
  const mail = branding?.email || dossier.entity.email;
  return (
    <div className="space-y-8">
      <Sectie titel="Uw winkel" id="k-winkel">
        <div className="k-panel flex flex-wrap gap-2">
          <p className="font-semibold w-full">{winkel}</p>
          {tel && <a className="k-button k-button--secondary w-full sm:w-auto" href={`tel:${tel}`}><KIcon naam="bel" /> {tel}</a>}
          {mail && <a className="k-button k-button--secondary w-full sm:w-auto break-all" href={`mailto:${mail}`}><KIcon naam="verstuur" /> {mail}</a>}
          {!tel && !mail && <p className="k-meta">— De winkel heeft geen contactgegevens ingevuld. Stuur uw vraag via Berichten.</p>}
        </div>
      </Sectie>
      <Activiteiten api={api} />
      {extra}
      <div className="k-meta space-y-1">
        <p>Laatst bijgewerkt: {datumLang(dossier.updatedAt)}</p>
        {branding && <p>Mogelijk gemaakt door <a className="k-link" href="https://c-bridge.app" target="_blank" rel="noopener noreferrer">{PLATFORM_NAAM}</a></p>}
      </div>
    </div>
  );
}

// ─── Berichten ─────────────────────────────────────────────

function KlantChat({ api, klantNaam, winkel, fases, hoogte }: {
  api: string; klantNaam: string; winkel: string; fases: Array<{ id: string; naam: string }>; hoogte: string;
}) {
  const [fase, setFase] = useState<string | undefined>(undefined);
  const { data: berichten = [], isLoading, isError, refetch } = usePubliekBerichten(api, fase);
  const verstuur = useSendPubliekBericht(api);
  const gelezen = useMarkeerGelezen(api);
  const [tekst, setTekst] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [verzonden, setVerzonden] = useState('');
  const lijst = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [volg, setVolg] = useState(true);
  const previews = useMemo(() => fotos.map((f) => URL.createObjectURL(f)), [fotos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  useEffect(() => { if (volg && lijst.current) lijst.current.scrollTop = lijst.current.scrollHeight; }, [berichten, volg]);
  // Berichten van de winkel als gelezen markeren (enkel zichtbaar gesprek; bestaande regel)
  useEffect(() => {
    for (const b of berichten) if (b.senderType !== 'customer' && !b.readAt) gelezen.mutate(b.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [berichten]);

  function afzender(b: BerichtPubliek) {
    if (b.senderType === 'customer') return b.vanMij === false ? (b.afzenderNaam || 'Klant') : 'U';
    if (b.sender) return `${b.sender.firstName} ${b.sender.lastName}`;
    if (b.senderType === 'system') return winkel;
    return 'Medewerker';
  }

  function stuur() {
    const inhoud = tekst.trim();
    if ((!inhoud && fotos.length === 0) || verstuur.isPending) return;
    setVerzonden('');
    // Concept blijft staan tot de server het bericht echt heeft opgeslagen.
    verstuur.mutate({ content: inhoud, naam: klantNaam, fase, fotos: fotos.length ? fotos : undefined }, {
      onSuccess: (b) => {
        setTekst(''); setFotos([]); setVolg(true);
        setVerzonden(`Verstuurd naar ${winkel} om ${new Date(b?.createdAt ?? Date.now()).toLocaleTimeString('nl-BE', { timeZone: TZ, hour: '2-digit', minute: '2-digit' })}.`);
      },
    });
  }

  return (
    <section aria-labelledby="k-berichten-titel" className="k-panel !p-0 flex flex-col overflow-hidden" style={{ height: hoogte }}>
      <div className="px-4 pt-4 pb-2 border-b border-[var(--k-border)]">
        <h2 id="k-berichten-titel" className="k-section-title">Berichten met {winkel}</h2>
        {fases.length > 0 && (
          <div className="mt-2">
            <span className="k-meta" id="k-fase-filter">Toon berichten over</span>
            <div className="flex gap-2 overflow-x-auto pb-1 mt-1" role="group" aria-labelledby="k-fase-filter">
              {[{ id: undefined as string | undefined, naam: 'Alles' }, ...fases].map((f) => (
                <button key={f.id ?? 'alles'} type="button" className="k-chip shrink-0" aria-pressed={fase === f.id} onClick={() => setFase(f.id)}>{f.naam}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div ref={lijst} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" tabIndex={0} aria-label="Gesprek"
        onScroll={() => { const el = lijst.current; if (el) setVolg(el.scrollHeight - el.scrollTop - el.clientHeight < 60); }}>
        {isLoading ? (
          <p className="k-meta" role="status">Berichten laden…</p>
        ) : isError ? (
          <div className="k-empty">
            <p className="k-empty__title">Berichten niet geladen</p>
            <p>Controleer uw verbinding.</p>
            <button type="button" className="k-button k-button--secondary" onClick={() => refetch()}>Opnieuw proberen</button>
          </div>
        ) : berichten.length === 0 ? (
          <div className="k-empty">
            <p className="k-empty__title">Nog geen berichten{fase ? ' over deze fase' : ''}</p>
            <p>Hebt u een vraag over uw keuken? Schrijf ze hieronder; {winkel} leest mee.</p>
          </div>
        ) : berichten.map((b) => {
          const ik = b.senderType === 'customer' && b.vanMij !== false;
          return (
            <div key={b.id} className={clsx('flex', ik ? 'justify-end' : 'justify-start')}>
              <div className={clsx('max-w-[85%] px-4 py-2 rounded-[var(--k-radius-panel)] border', ik ? 'rounded-br-[4px]' : 'rounded-bl-[4px]')}
                style={ik ? { background: 'var(--k-brand)', color: 'var(--k-on-brand)', borderColor: 'var(--k-brand)' } : { background: 'var(--k-surface)', borderColor: 'var(--k-border)' }}>
                <p className="text-[13px] leading-5 font-semibold">{afzender(b)}</p>
                <p className="whitespace-pre-wrap break-words">{zonderEmoji(b.content) || b.content}</p>
                {b.attachments.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {b.attachments.map((att) => (
                      <li key={att.id} className="flex items-center gap-2 text-[13px] leading-5">
                        <KIcon naam={att.mimeType.startsWith('image/') ? 'fotos' : 'bijlage'} size={16} />
                        {att.link
                          ? <a href={att.link} target="_blank" rel="noopener noreferrer" className="truncate underline">{att.filename}</a>
                          : <span className="truncate">{att.filename}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[13px] leading-5 mt-1 opacity-80">{tijdstip(b.createdAt)}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-4 py-3 border-t border-[var(--k-border)] space-y-2">
        {fotos.length > 0 && (
          <ul className="flex gap-3 overflow-x-auto pt-2" aria-label="Gekozen foto's">
            {previews.map((src, i) => (
              <li key={src} className="relative shrink-0">
                <img src={src} alt={`Foto ${i + 1}`} className="w-16 h-16 object-cover rounded-[var(--k-radius-control)] border border-[var(--k-border)]" />
                <button type="button" className="k-button k-button--icon !absolute -top-2 -right-2 !w-11 !min-h-[44px]" aria-label={`Foto ${i + 1} weghalen`}
                  onClick={() => setFotos((v) => v.filter((_, j) => j !== i))}><KIcon naam="sluit" /></button>
              </li>
            ))}
          </ul>
        )}
        <label htmlFor="k-bericht-invoer" className="k-sr-only">Uw bericht</label>
        <div className="flex items-end gap-2">
          <input ref={fileInput} type="file" accept="image/*" multiple className="k-sr-only" tabIndex={-1} aria-hidden="true"
            onChange={(e) => { const n = Array.from(e.target.files || []); setFotos((v) => [...v, ...n].slice(0, 5)); e.target.value = ''; }} />
          <button type="button" className="k-button k-button--icon shrink-0" aria-label="Foto toevoegen" onClick={() => fileInput.current?.click()}>
            <KIcon naam="fotos" />
          </button>
          <textarea id="k-bericht-invoer" rows={1} maxLength={2000} value={tekst} className="k-field !min-h-[44px] flex-1 resize-none"
            placeholder={fase ? 'Over deze fase…' : 'Uw bericht…'}
            onChange={(e) => { setTekst(e.target.value); setVerzonden(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); stuur(); } }} />
          <button type="button" className="k-button !w-11 !px-0 shrink-0" onClick={stuur} aria-busy={verstuur.isPending || undefined}
            aria-label={verstuur.isPending ? 'Bezig met versturen' : 'Versturen'}
            disabled={(!tekst.trim() && fotos.length === 0) || verstuur.isPending}>
            <KIcon naam="verstuur" />
          </button>
        </div>
        <p aria-live="polite" className={clsx('text-[13px] leading-5', verstuur.isError ? 'k-error' : 'k-meta')}>
          {verstuur.isError ? 'Niet verstuurd. Uw bericht staat er nog; probeer opnieuw.' : verzonden}
        </p>
      </div>
    </section>
  );
}

// ─── Mobiele voet ──────────────────────────────────────────

type View = 'keuken' | 'berichten' | 'documenten' | 'meer';

function KlantVoet({ view, setView, ongelezen, openVragen }: { view: View; setView: (v: View) => void; ongelezen: number; openVragen: number }) {
  const items: Array<{ key: View; icoon: KIconNaam; label: string; badge: number; sr: string }> = [
    { key: 'keuken', icoon: 'plaatsing', label: 'Mijn keuken', badge: openVragen, sr: 'open vraag' },
    { key: 'berichten', icoon: 'gesprekken', label: 'Berichten', badge: ongelezen, sr: 'ongelezen' },
    { key: 'documenten', icoon: 'documenten', label: 'Documenten', badge: 0, sr: '' },
    { key: 'meer', icoon: 'meer', label: 'Meer', badge: 0, sr: '' },
  ];
  return (
    <nav aria-label="Portaal" className="k-mobile-nav">
      {items.map((it) => (
        <button key={it.key} type="button" className="k-mobile-nav__item" aria-current={view === it.key ? 'page' : undefined}
          onClick={() => { setView(it.key); window.scrollTo({ top: 0 }); }}>
          <KIcon naam={it.icoon} size={24} />
          <span>{it.label}</span>
          {it.badge > 0 && (
            <span className="absolute top-2 left-1/2 ml-2 min-w-[20px] px-1 rounded-full text-[13px] leading-5 font-semibold text-center"
              style={{ background: 'var(--k-brand)', color: 'var(--k-on-brand)' }}>
              {it.badge > 99 ? '99+' : it.badge}<span className="k-sr-only"> {it.sr}</span>
            </span>
          )}
        </button>
      ))}
    </nav>
  );
}

// ─── Scherm ────────────────────────────────────────────────

export function KlantDossierScherm({ dossier, branding, api, meerExtra }: {
  dossier: DossierPubliek;
  branding: BrandingPubliek | null | undefined;
  /** Basispad van de dossier-API, bv. portaalApi(token) of dossierApi(id). */
  api: string;
  /** Extra blok onder Meer, bv. de accountlinks in /mijn. */
  meerExtra?: ReactNode;
}) {
  const { data: berichten = [] } = usePubliekBerichten(api);
  const { data: afspraken = [] } = usePubliekAfspraken(api);
  const desktop = useIsDesktop();
  const [view, setView] = useState<View>('keuken');

  const winkel = branding?.bedrijfsNaam || dossier.entity.name || 'uw winkel';
  const stijl = useMemo(() => merkVariabelen(branding?.kleurPrimair) as CSSProperties, [branding?.kleurPrimair]);
  const fases = dossier.trajectstappen.map((s) => ({ id: s.id, naam: stapNaam(s.template.naam) }));
  const ongelezen = berichten.filter((b) => b.senderType !== 'customer' && !b.readAt).length;
  const openVragen = afspraken.filter((a) => a.status === 'voorstel').length;

  const naarBerichten = () => {
    if (desktop) document.getElementById('k-bericht-invoer')?.focus();
    else { setView('berichten'); window.scrollTo({ top: 0 }); }
  };
  const naarDocumenten = () => { setView('documenten'); window.scrollTo({ top: 0 }); };
  const chatHoogte = desktop ? 'min(680px, calc(100dvh - 48px))' : 'calc(100dvh - 72px - env(safe-area-inset-bottom, 0px) - 57px - 24px)';

  const keuken = <MijnKeuken dossier={dossier} afspraken={afspraken} api={api} winkel={winkel} desktop={desktop} naarBerichten={naarBerichten} naarDocumenten={naarDocumenten} />;
  const chat = <KlantChat api={api} klantNaam={dossier.klantNaam} winkel={winkel} fases={fases} hoogte={chatHoogte} />;
  const docs = <Documenten documenten={dossier.documenten} api={api} winkel={winkel} />;
  const meer = <Meer dossier={dossier} branding={branding} api={api} winkel={winkel} extra={meerExtra} />;

  return (
    <div className="k-ui min-h-[100dvh]" style={stijl}>
      <a href="#inhoud" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 k-button">Naar de inhoud</a>
      <PortaalKop dossier={dossier} branding={branding} compact={!desktop && view !== 'keuken'} />
      <main id="inhoud" className={clsx('max-w-5xl mx-auto px-4 md:px-6', desktop ? 'py-8' : view === 'berichten' ? 'pt-3 pb-[calc(84px+env(safe-area-inset-bottom))]' : 'pt-4 pb-[calc(96px+env(safe-area-inset-bottom))]')}>
        {desktop ? (
          <div className="grid grid-cols-[minmax(0,3fr)_minmax(320px,2fr)] gap-8 items-start">
            <div className="space-y-10 min-w-0">{keuken}{docs}{meer}</div>
            <aside className="sticky top-6" aria-label="Berichten">{chat}</aside>
          </div>
        ) : (
          <>
            {view === 'keuken' && keuken}
            {view === 'berichten' && chat}
            {view === 'documenten' && docs}
            {view === 'meer' && meer}
          </>
        )}
      </main>
      {!desktop && <KlantVoet view={view} setView={setView} ongelezen={ongelezen} openVragen={openVragen} />}
    </div>
  );
}
